import qs from "qs"
import {pick, each} from "lodash"
import {OverridedMixpanel} from "mixpanel-browser"
import environment from "@irati/utils/env"
import {LOCAL, STAGING} from "@irati/utils/constants/environment"
import getConfig from "../../config"
import Tracker from "../../types/Tracker"
import {routeToProduct} from "../../products"
import tracker from "./index"

let mixpanel: OverridedMixpanel | null = null
let isGhostSession = false

const trackUrl: Tracker["trackUrl"] = ({previous, current}, attrs) => {
  const [, product] = Object.entries(routeToProduct).find(([path]) => current.path.startsWith(path)) ?? [null, null]

  if (mixpanel) {
    const data = {
      ...attrs,
      perkbox_product: product?.toLowerCase(),
      previous_url_path: previous?.path,
      previous_url_search: previous?.search,
      current_url_path: current.path,
      current_url_search: current.search
    }

    mixpanel.track("page_viewed", data)
  }
}

const utmTags = [
  "utm_term",
  "utm_campaign",
  "utm_medium",
  "utm_source",
  "utm_content",
  "utm_type",
  "utm_market",
  "utm_product",
  "utm_time"
]
/**
 * Reads query string from browser and registers any 'utm_*' strings to mixpanel for tracking.
 */
function setUTMParams(queryString: string) {
  if (mixpanel && queryString && queryString.includes("utm_")) {
    utmTags.forEach((element) => {
      mixpanel!.unregister(`${element} [last_touch]`)
    })

    const params: any = {}
    each(pick(qs.parse(queryString.replace("?", "")), utmTags), (value, key) => {
      params[`${key} [last_touch]`] = value
    })
    mixpanel.register(params)
    mixpanel.register({"utm_time [last_touch]": Date.now().toString()})
  }
}

function trackEvent(name: string, attributes: any) {
  if (mixpanel) {
    mixpanel.track(name, attributes)
  }
}

function checkUTMTimeout() {
  if (mixpanel) {
    const utmTimeLastTouch = mixpanel.get_property("utm_time [last_touch]")
    const utmTime = mixpanel.get_property("utm_time")

    if (utmTimeLastTouch) {
      removeUtmParams(utmTimeLastTouch, true)
    }

    if (utmTime) {
      removeUtmParams(utmTime, false)
    }
  }
}

function removeUtmParams(utmTime: string, isLastTouch: boolean) {
  const timeToReset = 6
  const utmDate = new Date(Number(utmTime))
  const hours = Math.abs(utmDate - new Date()) / 36e5
  if (hours >= timeToReset) {
    utmTags.forEach((element) => {
      mixpanel.unregister(isLastTouch ? `${element} [last_touch]` : element)
    })
  }
}

function handleLogin(attributes: any) {
  if (mixpanel) {
    mixpanel.identify(attributes.user_id)
    mixpanel.people.set(attributes)
    mixpanel.people.unset("$initial_referrer")
    // @ts-expect-error js specific file in shared environment
    const supports = CSS.supports
    mixpanel.track("login_user", {
      ...attributes,
      supports_container: supports("contain", "inline-size"),
      supports_gap: supports("gap", "2px"),
      supports_aspect_ratio: supports("aspect-ratio", "auto"),
      supports_selector_has: supports("selector(:has(+ a))"),
      supports_selector_where: supports("selector(:where(a))")
    })
  }
}

function handleLogout() {
  if (mixpanel) {
    mixpanel.track("logout_user", tracker.getDefaultAttributes())
    mixpanel.reset()
  }
}

function setAsGhostSession() {
  if (mixpanel && !isGhostSession) {
    isGhostSession = true
    const envName = environment.ENVIRONMENT_NAME
    const config = getConfig(envName)
    // token config value can be set but not in types
    mixpanel.set_config({token: config.mixpanel})
  }
}

const platformFunctions: Tracker = {
  setUTMParams,
  checkUTMTimeout,
  trackEvent,
  handleLogin,
  handleLogout,
  setAsGhostSession,
  setup,
  trackUrl
}

export default platformFunctions

function setup(env: string) {
  const config = getConfig(env)
  if (config.mixpanel) {
    // We use this kind of import because using 'import' for 'mixpanel-browser' causes side-effects which use XHRequest objects
    // which are not polyfilled into our test suite
    // eslint-disable-next-line global-require
    mixpanel = require("mixpanel-browser")
    mixpanel!.init(config.mixpanel, {
      debug: env === LOCAL || env === STAGING,
      property_blacklist: ["$referrer", "$initial_referrer"]
    })

    /* eslint-disable-next-line no-restricted-globals */
    setUTMParams(window.location.search)
    checkUTMTimeout()
  }
}
